import {
    ALLOW_FORWARDING,
    ALLOW_FORWARDING_IN_DOMAIN,
    APPLY_WATERMARK,
    PASSWORD_PROTECTION,
    PREVENT_DOWNLOAD_UNPROTECTED,
    PROTECT_ON_DOWNLOAD,
    PROTECT_AND_TRACK_DUMMY,
    REQUIRED_EMAIL_VERIFICATION,
    REQUIRED_PHONE_VERIFICATION,
    REQUIRED_PASSWORD_VERIFICATION,
    VIEWER_ONLY,
} from '@/consts';
import i18n from '@/content';

/**
 * Convert policy setting ID to i18n key format
 * (e.g., "dataRetention.Value" becomes "dataRetention_Value")
 */
const policySettingIdToI18Key = (settingId: string): string => settingId.split('.').join('_');

/**
 * Types of conflicts that can occur between policy settings
 */
export type ConflictType =
    | 'mutual_exclusion'  // Two settings cannot both be enabled
    | 'one_to_many'       // Enabling one setting disables multiple others
    | 'required_at_least_one'  // At least one setting from a group must be enabled
    | 'visibility'        // Hide certain settings based on another setting's state
    | 'enforced';         // Setting must always be in a specific state

/**
 * Defines a conflict between policy settings
 */
export interface ConflictRule {
    type: ConflictType;
    settings: string[];
    /** For conditional conflicts, which setting must be active to trigger the rule */
    triggerSetting?: string;
    /** For conditional conflicts, what state triggers the rule */
    triggerValue?: boolean;
    messageKey?: string;
    /** For enforced settings, the required value */
    enforcedValue?: boolean;
    /** Higher values are resolved first */
    priority?: number;
}

/**
 * Get user-friendly display name for a setting from i18n
 */
export const getSettingDisplayName = (settingId: string): string => {
    // Handle specific forwarding options (e.g., "forwardingOptions.withAnyone")
    if (settingId.startsWith('forwardingOptions.')) {
        const forwardingValue = settingId.split('.')[1];
        const valueKey = `policy.forwardingOptions.values.${forwardingValue}`;
        const valueTitle = i18n.t(valueKey);

        // If we have a translation for this specific value, use it
        if (valueTitle && valueTitle !== valueKey) {
            return valueTitle;
        }
    }

    const settingKey = policySettingIdToI18Key(settingId);
    const key = `policy.${settingKey}.title`;
    const title = i18n.t(key);

    // If translation failed (returns the key), return a formatted version of the settingId
    if (title === key || !title) {
        // Convert camelCase to Title Case as fallback
        return settingId
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    }

    return title;
};

/**
 * All conflict rules for policy settings
 * Migrated from old INCOMPATIBLE_ACCESS_SETTINGS and new requirements
 */
export const CONFLICT_RULES: ConflictRule[] = [
    // Mutual Exclusion: Password Protection ↔ Watermark
    {
        type: 'mutual_exclusion',
        settings: [PASSWORD_PROTECTION, APPLY_WATERMARK],
        messageKey: 'conflictSingle',
        priority: 10,
    },

    // Mutual Exclusion: Password Protection ↔ IRM Encryption
    {
        type: 'mutual_exclusion',
        settings: [PASSWORD_PROTECTION, PROTECT_ON_DOWNLOAD],
        messageKey: 'conflictSingle',
        priority: 10,
    },

    // Mutual Exclusion: Password Protection ↔ Allow Forwarding
    {
        type: 'mutual_exclusion',
        settings: [PASSWORD_PROTECTION, ALLOW_FORWARDING],
        messageKey: 'conflictSingle',
        priority: 10,
    },

    // Mutual Exclusion: Password Protection ↔ Allow Forwarding In Domain
    {
        type: 'mutual_exclusion',
        settings: [PASSWORD_PROTECTION, ALLOW_FORWARDING_IN_DOMAIN],
        messageKey: 'conflictSingle',
        priority: 10,
    },

    // One-to-Many: Block Download disables Password Protection and IRM
    {
        type: 'one_to_many',
        settings: [VIEWER_ONLY, PASSWORD_PROTECTION, PROTECT_ON_DOWNLOAD],
        triggerSetting: VIEWER_ONLY,
        triggerValue: true,
        messageKey: 'conflictMultiple',
        priority: 20,
    },
    
    // Required At Least One: Verification methods
    {
        type: 'required_at_least_one',
        settings: [
            REQUIRED_EMAIL_VERIFICATION,
            REQUIRED_PHONE_VERIFICATION,
            REQUIRED_PASSWORD_VERIFICATION,
        ],
        messageKey: 'atLeastOneVerification',
        priority: 5,
    },
    
    // Required At Least One: If blocking unencrypted, need at least one encryption method
    {
        type: 'required_at_least_one',
        settings: [PASSWORD_PROTECTION, PROTECT_ON_DOWNLOAD],
        triggerSetting: PREVENT_DOWNLOAD_UNPROTECTED,
        triggerValue: true,
        messageKey: 'atLeastOneEncryption',
        priority: 5,
    },
    
    // Visibility: Hide encryption settings when Block Download is ON
    {
        type: 'visibility',
        settings: [PASSWORD_PROTECTION, PROTECT_ON_DOWNLOAD, PREVENT_DOWNLOAD_UNPROTECTED],
        triggerSetting: VIEWER_ONLY,
        triggerValue: true,
        priority: 15,
    },
    
    // Enforced: Protect and Track is always ON
    {
        type: 'enforced',
        settings: [PROTECT_AND_TRACK_DUMMY],
        enforcedValue: true,
        priority: 100,
    },
];

/**
 * Get all mutual exclusion conflicts for a specific setting
 */
export const getMutualExclusionConflicts = (settingId: string): ConflictRule[] => {
    return CONFLICT_RULES.filter(
        (rule) => rule.type === 'mutual_exclusion' && rule.settings.includes(settingId)
    );
};

/**
 * Get one-to-many conflicts where this setting is the trigger
 */
export const getOneToManyConflicts = (settingId: string): ConflictRule[] => {
    return CONFLICT_RULES.filter(
        (rule) => rule.type === 'one_to_many' && rule.triggerSetting === settingId
    );
};

/**
 * Get required-at-least-one rules that include this setting
 */
export const getRequiredAtLeastOneRules = (settingId: string): ConflictRule[] => {
    return CONFLICT_RULES.filter(
        (rule) => rule.type === 'required_at_least_one' && rule.settings.includes(settingId)
    );
};

/**
 * Check if a setting is enforced to a specific value
 */
export const getEnforcedValue = (settingId: string): boolean | undefined => {
    const rule = CONFLICT_RULES.find(
        (r) => r.type === 'enforced' && r.settings.includes(settingId)
    );
    return rule?.enforcedValue;
};

/**
 * Get visibility rules that affect this setting
 */
export const getVisibilityRules = (settingId: string): ConflictRule[] => {
    return CONFLICT_RULES.filter(
        (rule) => rule.type === 'visibility' && rule.settings.includes(settingId)
    );
};
